<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// The dark brand panel beside TalkRobin's auth forms: a small "live"
// Monitoring preview, like the hero on the marketing site.
const props = defineProps({
  title: { type: String, required: true },
  highlight: { type: String, default: '' },
});

const { t } = useI18n();

// Illustrative visitors for the animation.
const SAMPLE_VISITORS = [
  { country: 'us', city: 'New York', page: '/pricing', source: 'Google' },
  { country: 'gb', city: 'London', page: '/features', source: 'LinkedIn' },
  { country: 'us', city: 'San Francisco', page: '/', source: 'Direct' },
  { country: 'ca', city: 'Toronto', page: '/blog/live-chat', source: 'Google' },
  { country: 'us', city: 'Austin', page: '/pricing', source: 'Facebook' },
  { country: 'de', city: 'Berlin', page: '/integrations', source: 'Direct' },
  { country: 'au', city: 'Sydney', page: '/demo', source: 'Newsletter' },
];
const VISIBLE_ROWS = 4;
const TICK_MS = 2600;

const rows = ref(
  SAMPLE_VISITORS.slice(0, VISIBLE_ROWS).map((v, i) => ({
    ...v,
    id: i,
    seconds: 40 + i * 37,
  }))
);
const toast = ref(null);
let next = VISIBLE_ROWS;
let timer = null;

const clock = s =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const tick = () => {
  rows.value.forEach(row => {
    row.seconds += Math.round(TICK_MS / 1000);
  });
  const visitor = SAMPLE_VISITORS[next % SAMPLE_VISITORS.length];
  const row = { ...visitor, id: next, seconds: 0 };
  next += 1;
  rows.value = [row, ...rows.value].slice(0, VISIBLE_ROWS);
  toast.value = row;
};

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  timer = setInterval(tick, TICK_MS);
});
onBeforeUnmount(() => clearInterval(timer));

const onlineCount = computed(() => 12 + (next % 5));
</script>

<template>
  <div
    class="relative flex flex-col w-full h-full overflow-hidden text-white rounded-[28px] bg-robin-navy"
  >
    <div
      class="absolute rounded-full pointer-events-none -top-40 -right-24 size-[520px] bg-robin-indigo/40 blur-[120px]"
    />
    <div
      class="absolute rounded-full pointer-events-none -bottom-48 -left-24 size-[420px] bg-robin-orange/20 blur-[120px]"
    />
    <div
      class="absolute inset-0 pointer-events-none opacity-[0.07] bg-[linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]"
    />

    <div class="relative flex flex-col h-full px-10 py-10 xl:px-14 xl:py-12">
      <span
        class="inline-flex items-center self-start gap-2 px-3 py-1.5 text-xs font-medium border rounded-full border-white/10 bg-white/5 text-white/80"
      >
        <span class="relative flex size-2">
          <span
            class="absolute inline-flex w-full h-full rounded-full opacity-75 bg-robin-orange motion-safe:animate-ping"
          />
          <span class="relative inline-flex rounded-full size-2 bg-robin-orange" />
        </span>
        {{ t('TALKROBIN_AUTH.SHOWCASE.EYEBROW') }}
      </span>

      <h2
        class="mt-5 font-heading text-[36px] xl:text-[44px] [@media(max-height:760px)]:text-[32px] font-bold leading-[1.02] tracking-[-0.045em] max-w-[520px]"
      >
        {{ props.title }}
        <span
          v-if="props.highlight"
          class="text-transparent bg-clip-text bg-gradient-to-r from-robin-indigo-light to-robin-orange"
        >
          {{ props.highlight }}
        </span>
      </h2>

      <div class="relative flex-1 min-h-[220px] mt-8">
        <div
          class="absolute inset-x-0 top-0 overflow-hidden border rounded-2xl border-white/10 bg-robin-ink/80 backdrop-blur shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
        >
          <div
            class="flex items-center justify-between px-5 py-3.5 border-b border-white/10"
          >
            <span class="inline-flex items-center gap-2 text-sm font-semibold">
              <Icon icon="i-lucide-radar" class="size-4 text-robin-indigo-light" />
              {{ t('TALKROBIN_AUTH.SHOWCASE.MONITORING') }}
            </span>
            <span
              class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-robin-success/15 text-robin-success"
            >
              <span class="rounded-full size-1.5 bg-robin-success" />
              {{ t('TALKROBIN_AUTH.SHOWCASE.ONLINE', { count: onlineCount }) }}
            </span>
          </div>
          <TransitionGroup
            tag="ul"
            class="relative m-0 p-0 list-none"
            enter-active-class="transition duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
            enter-from-class="opacity-0 -translate-y-3 bg-robin-indigo/20"
            leave-active-class="absolute inset-x-0 transition duration-300"
            leave-to-class="opacity-0"
            move-class="transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
          >
            <li
              v-for="row in rows"
              :key="row.id"
              class="flex items-center gap-3 px-5 py-3 text-sm border-b border-white/5 last:border-0"
            >
              <span class="relative flex size-2 shrink-0">
                <span
                  v-if="row.seconds < 5"
                  class="absolute inline-flex w-full h-full rounded-full opacity-75 bg-robin-success motion-safe:animate-ping"
                />
                <span class="relative inline-flex rounded-full size-2 bg-robin-success" />
              </span>
              <Flag :country="row.country" class="rounded-sm" />
              <span class="w-28 truncate text-white/90">{{ row.city }}</span>
              <span class="flex-1 truncate text-robin-indigo-light">
                {{ row.page }}
              </span>
              <span
                class="hidden px-2 py-0.5 text-xs rounded-md xl:inline bg-white/5 text-white/60"
              >
                {{ row.source }}
              </span>
              <span class="w-10 text-right tabular-nums text-white/50">
                {{ clock(row.seconds) }}
              </span>
            </li>
          </TransitionGroup>
        </div>

        <Transition
          enter-active-class="transition duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
          enter-from-class="opacity-0 translate-y-4 scale-95"
          leave-active-class="transition duration-200"
          leave-to-class="opacity-0"
          mode="out-in"
        >
          <div
            v-if="toast"
            :key="toast.id"
            class="absolute right-0 flex items-start gap-3 p-4 text-sm bg-white shadow-2xl -bottom-2 xl:-right-6 w-72 rounded-2xl text-robin-navy"
          >
            <span
              class="grid rounded-xl size-9 place-items-center shrink-0 bg-robin-sky text-robin-indigo"
            >
              <Icon icon="i-lucide-bell-ring" class="size-4" />
            </span>
            <div class="min-w-0">
              <p class="m-0 font-semibold">
                {{ t('TALKROBIN_AUTH.SHOWCASE.NEW_VISITOR') }}
                <span class="ml-1 text-xs font-medium text-robin-success">
                  {{ t('TALKROBIN_AUTH.SHOWCASE.NOW') }}
                </span>
              </p>
              <p class="flex items-center gap-1.5 m-0 mt-0.5 text-robin-slate">
                <Flag :country="toast.country" class="rounded-sm" />
                {{ toast.city }} · {{ toast.page }}
              </p>
            </div>
          </div>
        </Transition>
      </div>

      <div class="relative mt-8">
        <slot>
          <ul
            class="flex flex-wrap m-0 p-0 list-none gap-x-6 gap-y-2 text-sm text-white/70"
          >
            <li
              v-for="key in ['FREE_PLAN', 'SETUP', 'NO_CARD']"
              :key="key"
              class="inline-flex items-center gap-2"
            >
              <Icon icon="i-lucide-check" class="size-4 text-robin-success" />
              {{ t(`TALKROBIN_AUTH.SHOWCASE.TRUST.${key}`) }}
            </li>
          </ul>
        </slot>
      </div>
    </div>
  </div>
</template>
